import React, { useRef, useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  IconButton,
  styled,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  FormHelperText,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUpload, FiTrash2 } from 'react-icons/fi';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bounce, toast } from 'react-toastify';

import Page from '../../../components/Page';
import { TextField } from 'src/components';
import { Studentimg } from 'src/images';
import { studentService } from 'src/services/student.service';
import { CreateStudentDTO } from 'src/types/student.types';
import palette from 'src/theme/palette';
import { useAuth } from 'src/context/auth/AuthContext';

import './../../styles/addstudent.css';
import '../../../styles/variables.css';

const GRADE_OPTIONS = [
  // from 1 to 12
  { value: '1', label: 'Grade 1' },
  { value: '2', label: 'Grade 2' },
  { value: '3', label: 'Grade 3' },
  { value: '4', label: 'Grade 4' },
  { value: '5', label: 'Grade 5' },
  { value: '6', label: 'Grade 6' },
  { value: '7', label: 'Grade 7' },
  { value: '8', label: 'Grade 8' },
  { value: '9', label: 'Grade 9' },
  { value: '10', label: 'Grade 10' },
  { value: '11', label: 'Grade 11' },
  { value: '12', label: 'Grade 12' },
];

const StyledButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderRadius: '8px',
  cursor: 'not-allowed',
  transition: 'all 0.2s',
  border: 'none',
  marginRight: '12px',
  opacity: 0.6,
  '& svg': {
    marginRight: '8px',
  },
}));

const StyledUploadButton = styled(StyledButton)({
  backgroundColor: palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: palette.primary.main,
  },
});

const StyledDeleteButton = styled(StyledButton)({
  backgroundColor: 'white',
  color: palette.primary.main,

  border: `1px solid ${palette.primary.main}`,
  '&:hover': {
    backgroundColor: 'white',
  },
});

const ActionButton = styled('button')(({ theme, variant = 'contained' }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  border: variant === 'outlined' ? `1px solid ${palette.primary.main}` : 'none',
  backgroundColor: variant === 'outlined' ? 'white' : palette.primary.main,
  color: variant === 'outlined' ? palette.primary.main : 'white',
  '&:hover': {
    backgroundColor: variant === 'outlined' ? palette.primary.lighter : palette.primary.dark,
  },
  '& svg': {
    marginRight: '8px',
  },
}));

export const studentValidationSchema = Yup.object({
  firstname: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]*$/, 'First name can only contain letters and spaces'),
  lastname: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]*$/, 'Last name can only contain letters and spaces'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  studentId: Yup.string().required('Student ID is required'),
  // Password defaults to the Student ID (see handleStudentIdChange), so it only
  // needs to satisfy Supabase's minimum length once a teacher customizes it.
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  grade: Yup.string().required('Grade is required'),
  // Optional; checked only when filled in.
  phonenumber: Yup.string().matches(/^\+?[1-9]\d{1,14}$/, {
    message: 'Invalid phone number format',
    excludeEmptyString: true,
  }),
});

interface FormValues {
  firstname: string;
  lastname: string;
  email: string;
  studentId: string;
  password: string;
  confirmPassword: string;
  grade: string;
  phonenumber: string;
}

export default function AddStudent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBaseRoute } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [profilePic, setProfilePic] = useState<File | null>(null);

  const createStudentMutation = useMutation({
    mutationFn: (values: CreateStudentDTO) => studentService.createStudent(values),
    onSuccess: (createdStudent) => {
      const studentId = createdStudent.id;
      toast.success('Student created successfully', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        transition: Bounce,
      });
      // if (profilePic) {
      //   handleProfilePicUpload(profilePic, createdStudent.user.id);
      // }
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['active-students'] });
      navigate(studentId ? getBaseRoute(`/view-student/${studentId}`) : getBaseRoute('/students'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      studentId: '',
      grade: '',
      phonenumber: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: studentValidationSchema,
    onSubmit: async (values) => {
      console.log('values:', values);
      createStudentMutation.mutate(values);
    },
  });

  // Tracks whether the teacher has typed their own password. Until they do, the
  // password and confirmation stay in sync with the Student ID.
  const passwordManuallyEdited = useRef(false);

  const handleStudentIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    if (!passwordManuallyEdited.current) {
      formik.setFieldValue('password', event.target.value);
      formik.setFieldValue('confirmPassword', event.target.value);
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    passwordManuallyEdited.current = true;
    formik.handleChange(event);
  };

  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setProfilePic(event.target.files[0]);
    }
  };

  const handleProfilePicUpload = async (file: File, studentId: string) => {
    console.log('studentId:', studentId);
    if (!file || !studentId) return;

    try {
      setLoading(true);
      console.log('studentId:', studentId);
      const publicUrl = await studentService.updateProfilePicture(studentId, file);

      formik.setFieldValue('photourl', publicUrl);
    } catch (error) {
      console.error('Error handling photo:', error);
      toast.error('Failed to update profile picture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page title="Add New Student">
      <Container maxWidth={false} className="_container">
        <Typography variant="h4" gutterBottom>
          Add New Student
        </Typography>

        <Box sx={{ mb: 4, mt: 3 }}>
          <Grid container spacing={2}>
            {profilePic ? (
              <img
                src={URL.createObjectURL(profilePic)}
                style={{ height: 100, width: 100, borderRadius: 100, border: '1px solid #ccc' }}
              />
            ) : (
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 100,
                  marginRight: 20,
                  marginTop: 10,
                  border: '1px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  color: '#ccc',
                  backgroundColor: '#f0f0f0',
                }}
              >
                {formik.values.firstname.charAt(0)}
              </div>
            )}
            <Grid item xs={12} sm={10} md={10} lg={10}>
              <Typography variant="subtitle1" color="text.secondary">
                Only support .JPG, .JPEG, or .PNG file.
              </Typography>

              <div className="upload_row">
                <input
                  type="file"
                  id="_upload_pic"
                  onChange={handleProfilePicChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <label className="_btn" htmlFor="_upload_pic" style={{ marginTop: 0 }}>
                  {loading ? ' Uploading...' : '  Upload New photo'}
                </label>
                <Typography
                  variant="subtitle1"
                  color={palette.primary.main}
                  sx={{ cursor: 'pointer', padding: 1 }}
                  onClick={() => {
                    formik.setFieldValue('photourl', '');
                    setProfilePic(null);
                  }}
                >
                  Delete Photo
                </Typography>
              </div>
              {fileError && (
                <Typography style={{ fontSize: 10, fontWeight: 700, marginTop: 10 }} color="error">
                  {fileError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            mt: 4,
            '& .MuiGrid-item': {
              '& .MuiTextField-root': {
                width: '100%',
              },
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                placeholder="Enter First Name"
                {...formik.getFieldProps('firstname')}
                error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Enter Last Name"
                {...formik.getFieldProps('lastname')}
                error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                placeholder="Enter Email"
                type="email"
                {...formik.getFieldProps('email')}
                error={Boolean(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Student ID"
                placeholder="Enter Student ID"
                {...formik.getFieldProps('studentId')}
                onChange={handleStudentIdChange}
                error={Boolean(formik.touched.studentId && formik.errors.studentId)}
                helperText={
                  (formik.touched.studentId && formik.errors.studentId) ||
                  "Also used as the student's initial password"
                }
                inputProps={{ maxLength: 20 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="_input_label" style={{ marginBottom: 10, display: 'inline-block' }}>
                Grade
              </label>
              <FormControl fullWidth error={Boolean(formik.touched.grade && formik.errors.grade)} variant="outlined">
                <MuiSelect
                  labelId="grade-select-label"
                  id="grade-select"
                  name="grade"
                  value={formik.values.grade}
                  onChange={formik.handleChange}
                  className="_custom_input"
                  onBlur={formik.handleBlur}
                  style={{ paddingLeft: 0, fontWeight: 'bold' }}
                  displayEmpty
                  sx={{
                    // height: 40,
                    height: 50,
                    '&.Mui-focused': {
                      border: `1px solid ${palette.primary.main}`,
                      height: 50,
                      outline: 'none',
                      '& fieldset': {
                        border: 'none',
                      },
                    },

                    '& .MuiOutlinedInput-notchedOutline': {
                      // borderColor: 'rgba(145, 158, 171, 0.32)',
                      legend: {
                        display: 'none',
                      },
                    },
                    '& .MuiSelect-select': {
                      // paddingTop: '8px',
                      // paddingBottom: '8px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.primary',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'error.main',
                    },
                    backgroundColor: 'background.paper',
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography color="text.secondary">Select Grade</Typography>;
                    }
                    const option = GRADE_OPTIONS.find((opt) => opt.value === selected);
                    return option ? option.label : '';
                  }}
                >
                  {GRADE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
                {formik.touched.grade && formik.errors.grade && (
                  <FormHelperText error>{formik.errors.grade}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                placeholder="Enter Phone Number (e.g., +1234567890)"
                {...formik.getFieldProps('phonenumber')}
                error={Boolean(formik.touched.phonenumber && formik.errors.phonenumber)}
                helperText={formik.touched.phonenumber && formik.errors.phonenumber}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Enter Password (min 6 characters)"
                {...formik.getFieldProps('password')}
                onChange={handlePasswordChange}
                error={Boolean(formik.touched.password && formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  ),
                  maxLength: 64,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm Password"
                placeholder="Confirm Password"
                {...formik.getFieldProps('confirmPassword')}
                onChange={handlePasswordChange}
                error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(getBaseRoute('/students'))}
                  style={{ width: 120, height: 48, color: 'var(--primary-main)' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createStudentMutation.isPending}
                  variant="contained"
                  style={{ width: 120, height: 48 }}
                >
                  {createStudentMutation.isPending ? 'Creating...' : 'Save'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Page>
  );
}
